import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CourseProfessor, ProfessorCourse } from '../../interfaces/interfaces';
import {
    Button,
    Form,
    FormField,
    Header,
    Icon,
    Modal,
    Select,
    DropdownProps,
} from 'semantic-ui-react';
import {
    fetchPresignedUrl,
    uploadSyllabus,
    confirmSyllabusUpload,
} from '../../../util/FetchSyllabusData';
import FileInput from '../FileInput';

interface SyllabusUploadModalProps {
    open: boolean;
    onClose: () => void;
    courses?: ProfessorCourse[];
    professors?: CourseProfessor[];
    onUploadComplete: () => void;
}

const SyllabusUploadModal: React.FC<SyllabusUploadModalProps> = ({
    open,
    onClose,
    professors,
    courses,
    onUploadComplete,
}) => {
    const [courseProfessorId, setCourseProfessorId] = useState<number | null>(
        null,
    );
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if (open) {
            setCourseProfessorId(null);
            setSelectedFile(null);
            setIsSubmitting(false);
        }
    }, [open]);

    const handleSelectChange = (
        _: React.SyntheticEvent<HTMLElement>,
        data: DropdownProps,
    ) => {
        setCourseProfessorId(data.value as number);
    };

    const handleSubmit = async () => {
        if (!courseProfessorId) {
            toast.error('Please select a course or professor.');
            return;
        }

        if (!selectedFile) {
            toast.error('Please select a file to upload.');
            return;
        }

        try {
            setIsSubmitting(true);

            const presignedResponse = await fetchPresignedUrl();
            if (!presignedResponse.ok) {
                throw new Error('Failed to get presigned URL.');
            }
            const { presigned_url, file_name } = await presignedResponse.json();

            const uploadResponse = await uploadSyllabus(
                selectedFile,
                presigned_url,
            );
            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file.');
            }

            const confirmationResponse = await confirmSyllabusUpload(
                file_name,
                courseProfessorId,
            );
            if (!confirmationResponse.ok) {
                throw new Error('Failed to confirm upload.');
            }

            toast.success('Syllabus uploaded successfully.');
            onUploadComplete();
            onClose();
        } catch (error) {
            console.error(error);
            toast.error('Failed to upload syllabus. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal onClose={onClose} open={open}>
            <Header icon>
                <Icon name="archive" />
                Submit New Syllabus
            </Header>

            <Modal.Content>
                <Form>
                    {courses && (
                        <FormField
                            control={Select}
                            options={courses.map((course) => ({
                                key: course.course_professor_id,
                                text: `[${course.course_code}] ${course.course_name}`,
                                value: course.course_professor_id,
                            }))}
                            placeholder="Select a Course"
                            onChange={handleSelectChange}
                        />
                    )}

                    {professors && (
                        <FormField
                            control={Select}
                            options={professors.map((professor) => ({
                                key: professor.course_professor_id,
                                text: `${professor.first_name} ${professor.last_name}`,
                                value: professor.course_professor_id,
                            }))}
                            placeholder="Select a Professor"
                            onChange={handleSelectChange}
                        />
                    )}
                </Form>

                <p>
                    Please drag your syllabus file below, or click to browse for
                    a file.
                </p>
                <FileInput onFileSelected={setSelectedFile} />
            </Modal.Content>

            <Modal.Actions>
                <Button basic color="red" onClick={onClose}>
                    <Icon name="remove" /> Cancel
                </Button>
                <Button
                    color="green"
                    loading={isSubmitting}
                    onClick={handleSubmit}
                >
                    <Icon name="checkmark" /> Submit
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default SyllabusUploadModal;
